import React from "react";
import PropTypes from "prop-types";
import { Box, IconButton, Typography, Select, MenuItem } from "@mui/material";
import {
    FirstPage as FirstPageIcon,
    LastPage as LastPageIcon,
    KeyboardArrowLeft,
    KeyboardArrowRight,
} from "@mui/icons-material";
import "../table/CommonTable.scss";

const CustomPagination = ({
    count,
    page,
    rowsPerPage = 100,
    onPageChange,
    onRowsPerPageChange,
}) => {
    const totalPages = Math.ceil(count / rowsPerPage);
    const [inputValue, setInputValue] = React.useState(page);

    React.useEffect(() => {
        setInputValue(page);
    }, [page]);

    const handlePageInputChange = (e) => {
        const value = e.target.value;
        if (value === '' || /^\d+$/.test(value)) {
            setInputValue(value);
        }
    };

    const handlePageInputBlur = () => {
        const numericValue = parseInt(inputValue, 10);
        if (!numericValue || numericValue < 1) {
            onPageChange(1);
            setInputValue(1);
        } else if (numericValue > totalPages) {
            onPageChange(totalPages);
            setInputValue(totalPages);
        } else {
            onPageChange(numericValue);
        }
    };

    return (
        <Box className="custom-pagination">
            <Box className="pagination-controls">
                <IconButton
                    onClick={() => onPageChange(1)}
                    disabled={page <= 1}
                >
                    <FirstPageIcon style={{ fontSize: "1rem" }}/>
                </IconButton>
                <IconButton
                    onClick={() => onPageChange(page - 1)}
                    disabled={page <= 1}
                >
                    <KeyboardArrowLeft style={{ fontSize: "1rem" }}/>
                </IconButton>

                <Box className="pagination-text">
                    <Typography component="span">
                        Page
                    </Typography>
                    <input
                        type="text"
                        value={inputValue}
                        onChange={handlePageInputChange}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handlePageInputBlur();
                            }
                            if (!/[0-9]/.test(e.key) && e.key !== 'Enter') {
                                e.preventDefault();
                            }
                        }}
                        onBlur={handlePageInputBlur}
                        className="page-input"
                    />
                    <Typography component="span">
                        of {totalPages}
                    </Typography>
                </Box>

                <IconButton
                    onClick={() => onPageChange(page + 1)}
                    disabled={page >= totalPages}
                >
                    <KeyboardArrowRight style={{ fontSize: "1rem" }}/>
                </IconButton>
                <IconButton
                    onClick={() => onPageChange(totalPages)}
                    disabled={page >= totalPages}
                >
                    <LastPageIcon style={{ fontSize: "1rem" }}/>
                </IconButton>
            </Box>
            <Box className="pagination-controls">
                <Typography component="span">
                    Rows per page
                </Typography>
                <Select
                    size="small"
                    value={rowsPerPage}
                    onChange={(e) => onRowsPerPageChange(Number(e.target.value))}
                    className="rows-per-page-select"
                    MenuProps={{
                        anchorOrigin: {
                            vertical: 'top',
                            horizontal: 'left',
                        },
                        transformOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                        },
                        PaperProps: {
                            sx: {
                                borderRadius: '4px',
                                marginBottom: '4px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            },
                        },
                    }}
                >
                    {[50, 100, 150, 200].map((rows) => (
                        <MenuItem key={rows} value={rows}>
                            {rows}
                        </MenuItem>
                    ))}
                </Select>
                <Typography component="span">
                    {`${(page - 1) * rowsPerPage + 1}-${Math.min(page * rowsPerPage, count)} of ${count}`}
                </Typography>
            </Box>
        </Box>
    );
};

CustomPagination.propTypes = {
    count: PropTypes.number.isRequired,
    page: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number,
    onPageChange: PropTypes.func.isRequired,
    onRowsPerPageChange: PropTypes.func.isRequired,
};

CustomPagination.defaultProps = {
    rowsPerPage: 100,
};

export default CustomPagination;
